import React, { useState, useEffect } from "react";
import {
  getAllTeamMembers,
  deleteTeamMember,
  updateTeamOrder,
  updateTeamMemberPhoto,
} from "../../../services/teamServices";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaPlus, FaSave, FaTimes } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreateTeamMemberModal from "./CreateTeamMemberModal";
import EditTeamMemberModal from "./EditTeamMemberModal";

/**
 * TeamManagement (UI/UX refonte)
 * - Corrige le décalage de colonnes (suppression du <td> fantôme)
 * - Améliore l’affichage de la photo + overlay "Changer la photo" AU SURVOL
 * - Responsive + dark mode
 */
const TeamManagement = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Ne convertir en URL absolue que les fichiers côté serveur (/uploads/...)
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  useEffect(() => {
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const data = await getAllTeamMembers();
      setTeam(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des membres de l'équipe",
        error
      );
      toast.error("Impossible de charger l’équipe.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeamMember = async (member) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer ${member.nom} ${member.prenom} ?`
      )
    ) {
      try {
        await deleteTeamMember(member.id);
        toast.success("Membre supprimé avec succès");
        fetchTeamMembers();
      } catch (error) {
        toast.error("Erreur lors de la suppression d’un membre");
        console.error("Erreur lors de la suppression d’un membre", error);
      }
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setSelectedMember(null);
    setIsEditModalOpen(false);
  };

  const openUploadModal = (member) => {
    setSelectedMember(member);
    setSelectedFile(null);
    setIsUploadModalOpen(true);
  };
  const closeUploadModal = () => {
    setSelectedMember(null);
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  const moveTeamMemberUp = async (index) => {
    if (index === 0) return;
    const newTeam = [...team];
    const tmp = newTeam[index - 1];
    newTeam[index - 1] = newTeam[index];
    newTeam[index] = tmp;
    newTeam[index - 1].position--;
    newTeam[index].position++;
    setTeam(newTeam);
    await updateTeamOrder(newTeam);
    fetchTeamMembers();
  };

  const moveTeamMemberDown = async (index) => {
    if (index === team.length - 1) return;
    const newTeam = [...team];
    const tmp = newTeam[index + 1];
    newTeam[index + 1] = newTeam[index];
    newTeam[index] = tmp;
    newTeam[index + 1].position++;
    newTeam[index].position--;
    setTeam(newTeam);
    await updateTeamOrder(newTeam);
    fetchTeamMembers();
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedMember) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      await updateTeamMemberPhoto(selectedMember.id, selectedFile, token);
      await fetchTeamMembers();
      toast.success("Photo mise à jour avec succès !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image :", error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  const defaultAvatar = "/img/user_avatar.jpg";

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-3 sm:px-6">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                  Gestion des Membres de l’Équipe
                </h1>
                <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                  Ajoutez, mettez à jour et organisez l’affichage de votre
                  équipe.
                </p>
              </div>
              <Button
                text="Ajouter un membre"
                icon={FaPlus}
                onClick={() => setIsCreateModalOpen(true)}
                className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-1"
                hoverLabel="Ajouter un nouveau membre"
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {/* Loading */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 flex items-center gap-4 bg-white/60 dark:bg-slate-900/40"
                  >
                    <div className="h-16 w-16 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-2/5 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/3 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/4 bg-slate-200 dark:bg-slate-700 rounded" />
                    </div>
                    <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                ))}
              </div>
            )}

            {!loading && team.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun membre pour le moment
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Cliquez sur “Ajouter un membre” pour commencer.
                </p>
              </div>
            )}

            {!loading && team.length > 0 && (
              <>
                {/* Desktop table */}
                <div className="hidden md:block">
                  <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {[
                            "Photo",
                            "Nom",
                            "Prénom",
                            "Fonction",
                            "Catégorie",
                            "Position",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {team.map((member, index) => (
                          <tr
                            key={member.id}
                            className="group transition-all duration-150 hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            {/* PHOTO */}
                            <td className="px-4 py-3 align-middle">
                              <button
                                onClick={() => openUploadModal(member)}
                                className="relative inline-flex items-center justify-center"
                                title="Changer la photo"
                              >
                                <img
                                  src={toAbsolute(member.photoURL) || defaultAvatar}
                                  alt={`${member.nom} ${member.prenom}`}
                                  onError={(e) => {
                                    e.currentTarget.src = defaultAvatar;
                                  }}
                                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                                />
                                {/* Overlay au SURVOL UNIQUEMENT */}
                                <div className="pointer-events-none absolute inset-0 rounded-full flex items-center justify-center bg-slate-900/0 opacity-0 transition group-hover:bg-slate-900/50 group-hover:opacity-100">
                                  <span className="text-[11px] sm:text-xs font-medium text-white px-2 py-1 rounded-full bg-black/40">
                                    Changer la photo
                                  </span>
                                </div>
                              </button>
                            </td>

                            {/* NOM */}
                            <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {member.nom}
                            </td>

                            {/* PRÉNOM */}
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {member.prenom}
                            </td>

                            {/* FONCTION */}
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {member.fonction || "—"}
                            </td>

                            {/* CATÉGORIE */}
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {member.categorie || "—"}
                            </td>

                            {/* POSITION + flèches */}
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              <div className="inline-flex items-center gap-2">
                                <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                                  {member.position}
                                </span>
                                <div className="flex flex-col -my-1">
                                  <button
                                    onClick={() => moveTeamMemberUp(index)}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                    title="Monter"
                                  >
                                    <TbTriangleFilled />
                                  </button>
                                  <button
                                    onClick={() => moveTeamMemberDown(index)}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                    title="Descendre"
                                  >
                                    <TbTriangleInvertedFilled />
                                  </button>
                                </div>
                              </div>
                            </td>

                            {/* ACTIONS */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ButtonEdit
                                  onClick={() => openEditModal(member)}
                                />
                                <ButtonDelete
                                  onClick={() => handleDeleteTeamMember(member)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {team.map((member, index) => (
                    <div
                      key={`card-${member.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => openUploadModal(member)}
                          className="relative shrink-0"
                          title="Changer la photo"
                        >
                          <img
                            src={toAbsolute(member.photoURL) || defaultAvatar}
                            alt={`${member.nom} ${member.prenom}`}
                            onError={(e) => {
                              e.currentTarget.src = defaultAvatar;
                            }}
                            className="h-16 w-16 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                          />
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {member.prenom} {member.nom}
                            </h3>
                            <div className="flex items-center gap-1">
                              <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                                {member.position}
                              </span>
                              <div className="flex flex-col -my-1">
                                <button
                                  onClick={() => moveTeamMemberUp(index)}
                                  className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                  title="Monter"
                                >
                                  <TbTriangleFilled />
                                </button>
                                <button
                                  onClick={() => moveTeamMemberDown(index)}
                                  className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                  title="Descendre"
                                >
                                  <TbTriangleInvertedFilled />
                                </button>
                              </div>
                            </div>
                          </div>
                          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                            {member.fonction || "—"}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {member.categorie || "—"}
                          </p>

                          <div className="mt-4 flex items-center gap-2">
                            <ButtonEdit onClick={() => openEditModal(member)} />
                            <ButtonDelete
                              onClick={() => handleDeleteTeamMember(member)}
                            />
                            {/* En mobile, on met un lien explicite (pas de hover) */}
                            <button
                              onClick={() => openUploadModal(member)}
                              className="ml-auto text-xs underline decoration-dotted underline-offset-4 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
                            >
                              Changer la photo
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreateTeamMemberModal
          isActive={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshTeam={fetchTeamMembers}
        />
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <EditTeamMemberModal
          isActive={isEditModalOpen}
          onClose={closeEditModal}
          refreshTeam={fetchTeamMembers}
          memberData={selectedMember}
        />
      )}

      {/* Upload Photo Modal */}
      {isUploadModalOpen && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeUploadModal}
          />
          <div className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
              <h2 className="text-white text-lg font-bold">
                Changer la photo — {selectedMember.nom} {selectedMember.prenom}
              </h2>
            </div>

            {/* Modal Body */}
            <div className="bg-white dark:bg-slate-900 p-6">
              <form onSubmit={handleUploadSubmit} className="space-y-5">
                {/* Preview */}
                <div className="flex items-center gap-4">
                  <img
                    src={toAbsolute(selectedMember.photoURL) || defaultAvatar}
                    alt="Actuelle"
                    onError={(e) => {
                      e.currentTarget.src = defaultAvatar;
                    }}
                    className="h-16 w-16 rounded-full object-cover shadow ring-2 ring-slate-200 dark:ring-slate-700"
                  />
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    <p className="font-medium">Photo actuelle</p>
                    <p className="text-xs opacity-80">
                      Formats acceptés : JPG, PNG, WebP
                    </p>
                  </div>
                </div>

                {/* File input */}
                <label
                  htmlFor="upload-file"
                  className="block text-sm font-medium text-slate-800 dark:text-slate-100"
                >
                  Nouvelle image
                </label>
                <input
                  id="upload-file"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm file:mr-4 file:rounded-xl file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:font-semibold file:text-slate-800 hover:file:bg-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100 dark:hover:file:bg-slate-700"
                />

                {/* Selected preview */}
                {selectedFile && (
                  <div className="flex items-center gap-4">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Aperçu"
                      className="h-16 w-16 rounded-full object-cover ring-2 ring-violet-400/70"
                    />
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      {selectedFile.name} —{" "}
                      {(selectedFile.size / 1024).toFixed(0)} Ko
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    text="Fermer"
                    onClick={closeUploadModal}
                    icon={FaTimes}
                    className="gap-2 !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl"
                    type="button"
                  />
                  <Button
                    text={isUploading ? "Envoi..." : "Sauvegarder"}
                    type="submit"
                    icon={FaSave}
                    className="gap-2 !rounded-xl"
                    disabled={isUploading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </section>
  );
};

export default TeamManagement;
